import { Link } from "react-router-dom";
import { Users, Wallet, Car, MessageSquare } from "lucide-react";
import { fmtDate, fmtCurrency } from "../../lib/status";
import { Info } from "./customerUtils";

export function CustomerOverviewTab({ profile }) {
  const c = profile.customer || {};
  const rc = profile.returning_customer || {};
  const dep = profile.deposits || {};

  return (
    <div className="space-y-4" data-testid="cp-overview">
      <div className="rounded-xl border border-slate-200 bg-white p-6 grid sm:grid-cols-4 gap-4 text-sm">
        <Info label="Name" value={c.name || "-"} />
        <Info label="Email" value={c.email} />
        <Info label="Phone" value={c.phone || "-"} />
        <Info label="Preferred contact" value={c.preferred_channel || "-"} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cp-summary-cards">
        <SummaryCard tone="emerald" icon={Users} label="Returning customer"
                     value={rc.is_returning ? `${rc.booking_count} bookings` : "First booking"}
                     sub={rc.is_returning ? "Across all statuses" : "No prior bookings on file"} />
        <SummaryCard tone="blue" icon={Wallet} label="Total recorded value"
                     value={fmtCurrency(profile.total_recorded_value)}
                     sub="Sum of completed bookings' price" />
        <SummaryCard tone="slate" icon={Car} label="Vehicles rented"
                     value={String((profile.vehicles_rented || []).length)}
                     sub={(profile.vehicles_rented || []).map((v) => v.name).filter(Boolean).slice(0, 3).join(", ") || "-"} />
        <SummaryCard tone="slate" icon={MessageSquare} label="Last rental"
                     value={profile.last_rental_at ? fmtDate(profile.last_rental_at) : "-"}
                     sub="Most recent completed booking" />
      </div>

      {(dep.required_total > 0 || dep.collected_total > 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="cp-overview-deposits">
          <h3 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Deposits (all bookings)</h3>
          <dl className="mt-3 grid sm:grid-cols-4 gap-4 text-sm">
            <Info label="Required" value={fmtCurrency(dep.required_total)} />
            <Info label="Collected" value={fmtCurrency(dep.collected_total)} />
            <Info label="Refunded" value={fmtCurrency(dep.refunded_total)} />
            <Info label="Outstanding" value={fmtCurrency(dep.outstanding_total)} />
          </dl>
        </div>
      )}

      {(profile.change_requests || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="cp-overview-change-requests">
          <h3 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-amber-800">Change requests</h3>
          <ul className="mt-2 space-y-1.5 text-sm">
            {profile.change_requests.map((cr) => (
              <li key={cr.booking_id}>
                <Link to={`/app/bookings/${cr.booking_id}`} className="text-amber-900 hover:underline font-medium">
                  {cr.booking_reference}
                </Link>
                <span className="text-amber-800/80 ml-2">
                  {cr.change_requested ? "Change requested" : ""}
                  {cr.change_requested && cr.reconfirmation_required ? " · " : ""}
                  {cr.reconfirmation_required ? "Reconfirmation required" : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ tone, icon: Icon, label, value, sub }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/70 text-emerald-900",
    blue: "border-sky-200 bg-sky-50/70 text-sky-900",
    slate: "border-slate-200 bg-white text-slate-800",
  };
  const iconTones = { emerald: "text-emerald-700", blue: "text-sky-700", slate: "text-slate-500" };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconTones[tone] || iconTones.slate}`} />
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">{label}</div>
      </div>
      <div className="mt-1 font-display text-lg font-bold truncate" title={value}>{value}</div>
      {sub && <div className="text-[11px] opacity-75 mt-0.5 line-clamp-2">{sub}</div>}
    </div>
  );
}
